import { Vector3 } from 'three';

import type { Skeleton, Bone } from '../skeleton/core';
import {
  FINGER_ROLE_NAMES,
  ROLE_TERMS,
  classifyToeParentName,
  getBaseRole,
  getExpectedSide,
  EXPECTED_CHILD_COUNTS,
  EXPECTED_DEPTHS,
  detectSideFromName,
  ROLE_HINTS,
  type SideConvention,
  sideConventionIsValid,
  sideConventionDetectSide,
} from '../schema/mapping';
import type { ScoringProfile } from './scoring';

// ─── Spatial data interfaces ────────────────────────────────────────────

export interface BranchingPoint {
  childCount: number;
  hasSymmetricChildren: boolean;
  hasDownwardChildren: boolean;
  hasLateralChildren: boolean;
  hasUpwardChildren: boolean;
}

export interface SpatialData {
  getVerticalPosition(boneIdx: number): number;
  getBranchingPoint(boneIdx: number): BranchingPoint | null;
  getSideFromSpatial(boneIdx: number, skeleton?: Skeleton): string | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────

function clamp(value: number, low = 0.0, high = 1.0): number {
  return Math.max(low, Math.min(high, value));
}

function vecLength(v: Vector3): number {
  return v.length();
}

function vecDot(a: Vector3, b: Vector3): number {
  return a.dot(b);
}

function vecNormalize(v: Vector3): Vector3 {
  const len = v.length();
  if (len < 1e-8) return new Vector3();
  return v.clone().divideScalar(len);
}

// ─── CheckContext ────────────────────────────────────────────────────────

export interface CheckContext {
  skeleton: Skeleton;
  boneIdx: number;
  role: string;

  chain?: number[] | null;
  mirrorChain?: number[] | null;
  expectedDirection?: Vector3 | null;
  parentBoneIdx?: number | null;
  chainPosition?: number | null;
  chainLength?: number | null;
  roleToBone?: Record<string, string> | null;
  lateralAxis?: number;
  upAxis?: number;
  sideConvention?: SideConvention | null;
  extra?: Record<string, any>;
}

function ctxBone(ctx: CheckContext): Bone {
  return ctx.skeleton.bone(ctx.boneIdx);
}

function ctxBoneName(ctx: CheckContext): string {
  return ctxBone(ctx).name;
}

function ctxBonePosition(ctx: CheckContext): Vector3 {
  return ctx.skeleton.getBoneWorldPosition(ctx.boneIdx);
}

function ctxLateralAxis(ctx: CheckContext): number {
  return ctx.lateralAxis ?? 0;
}

function ctxExtra(ctx: CheckContext): Record<string, any> {
  return ctx.extra ?? {};
}

// ─── CheckResult ────────────────────────────────────────────────────────

export class CheckResult {
  passed: boolean;
  score: number;
  reason: string;

  constructor(passed: boolean, score: number, reason: string) {
    this.passed = passed;
    this.score = score;
    this.reason = reason;
  }

  get state(): 'positive' | 'negative' | 'neutral' {
    if (this.score >= 0.7) return 'positive';
    if (this.score < 0.5) return 'negative';
    return 'neutral';
  }

  static success(score: number, reason: string): CheckResult {
    return new CheckResult(true, score, reason);
  }

  static failure(score: number, reason: string): CheckResult {
    return new CheckResult(false, score, reason);
  }

  static neutral(score: number, reason: string): CheckResult {
    return new CheckResult(score >= 0.5, score, reason);
  }
}

// ─── Check (abstract) ──────────────────────────────────────────────────

export abstract class Check {
  id: string;
  name: string;
  category: string;
  weight: number;
  required: boolean;
  weightKey: string | null;

  constructor(init: {
    id: string;
    name: string;
    category: string;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  }) {
    this.id = init.id;
    this.name = init.name;
    this.category = init.category;
    this.weight = init.weight ?? 1.0;
    this.required = init.required ?? false;
    this.weightKey = init.weightKey ?? null;
  }

  abstract evaluate(ctx: CheckContext): CheckResult;

  getEffectiveWeight(ctx: CheckContext): number {
    if (this.weightKey == null) return this.weight;
    const profile = ctxExtra(ctx).scoring_profile as ScoringProfile | undefined;
    if (profile != null) {
      const w = (profile.weights as unknown as Record<string, number>)[this.weightKey];
      return w ?? this.weight;
    }
    return this.weight;
  }
}

// ─── EvaluationResult ──────────────────────────────────────────────────

export class EvaluationResult {
  score: number;
  disqualified: boolean;
  results: [Check, CheckResult][];

  constructor(
    score: number,
    disqualified: boolean,
    results: [Check, CheckResult][],
  ) {
    this.score = score;
    this.disqualified = disqualified;
    this.results = results;
  }

  get passedChecks(): [Check, CheckResult][] {
    return this.results.filter(([, r]) => r.passed);
  }

  get failedChecks(): [Check, CheckResult][] {
    return this.results.filter(([, r]) => !r.passed);
  }

  getCheckResult(checkId: string): CheckResult | null {
    for (const [check, result] of this.results) {
      if (check.id === checkId) return result;
    }
    return null;
  }

  toEvidenceItems(): [string, string][] {
    return this.results.map(([, result]) => [result.state, result.reason]);
  }
}

// ═══════════════════════════════════════════════════════════════════════
// NAMING CHECKS
// ═══════════════════════════════════════════════════════════════════════

export class SideMatchCheck extends Check {
  expectedSide: string | null;
  useSpatialFallback: boolean;

  constructor(opts: {
    expectedSide?: string | null;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
    useSpatialFallback?: boolean;
  } = {}) {
    super({
      id: 'side_match',
      name: 'Side matches role',
      category: 'naming',
      weight: opts.weight ?? 0.5,
      required: opts.required ?? true,
      weightKey: opts.weightKey ?? null,
    });
    this.expectedSide = opts.expectedSide ?? null;
    this.useSpatialFallback = opts.useSpatialFallback ?? false;
  }

  evaluate(ctx: CheckContext): CheckResult {
    if (this.expectedSide == null) {
      return CheckResult.success(1.0, 'Center role (no side requirement)');
    }

    const detected = this._detectSideFromName(ctx);
    if (detected != null) {
      if (detected === this.expectedSide) {
        return CheckResult.success(1.0, `Side matches: ${detected} (from name)`);
      }
      return CheckResult.failure(
        0.0,
        `Wrong side: expected ${this.expectedSide}, found ${detected}`,
      );
    }

    if (this._shouldUseSpatialFallback(ctx)) {
      const spatialSide = this._detectSideFromSpatial(ctx);
      if (spatialSide != null) {
        if (spatialSide === this.expectedSide) {
          return CheckResult.success(
            0.85,
            `Side matches: ${spatialSide} (from position)`,
          );
        }
        return CheckResult.failure(
          0.1,
          `Wrong side: expected ${this.expectedSide}, position suggests ${spatialSide}`,
        );
      }
      return CheckResult.neutral(0.5, 'Side unclear (bone near center)');
    }

    return CheckResult.neutral(0.5, 'Side not detected from name');
  }

  private _shouldUseSpatialFallback(ctx: CheckContext): boolean {
    if (this.useSpatialFallback) return true;
    return ctxExtra(ctx).use_spatial_side_fallback === true;
  }

  private _detectSideFromName(ctx: CheckContext): string | null {
    if (ctx.sideConvention && sideConventionIsValid(ctx.sideConvention)) {
      return sideConventionDetectSide(ctx.sideConvention, ctxBoneName(ctx));
    }
    const profile = ctxExtra(ctx).scoring_profile as ScoringProfile | undefined;
    if (profile != null && profile.mode === 'generic') return null;
    return detectSideFromName(ctxBoneName(ctx));
  }

  private _detectSideFromSpatial(ctx: CheckContext): string | null {
    const spatialData = ctxExtra(ctx).spatial_data as SpatialData | undefined;
    if (!spatialData) return null;
    return spatialData.getSideFromSpatial(ctx.boneIdx, ctx.skeleton);
  }
}

export class NamingMatchCheck extends Check {
  hints: string[];

  constructor(opts: {
    hints?: string[];
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'naming_match',
      name: 'Name matches role hints',
      category: 'naming',
      weight: opts.weight ?? 1.0,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.hints = opts.hints ?? [];
  }

  evaluate(ctx: CheckContext): CheckResult {
    const nameLower = ctxBoneName(ctx).toLowerCase();
    const baseRole = getBaseRole(ctx.role);
    const expectedSide = getExpectedSide(ctx.role);
    const terms = ROLE_TERMS[baseRole] ?? [];

    if (terms.length > 0) {
      const matchedTerms = terms.filter((t) => nameLower.includes(t.toLowerCase()));
      if (matchedTerms.length > 0) {
        if (expectedSide) {
          const detectedSide = this._detectSide(ctx, nameLower);
          if (detectedSide === expectedSide) {
            return CheckResult.success(
              1.0,
              `Name matches: ${matchedTerms[0]} (${detectedSide})`,
            );
          } else if (detectedSide == null) {
            return CheckResult.neutral(
              0.6,
              `Term match: ${matchedTerms[0]} (side unclear)`,
            );
          } else {
            return CheckResult.failure(
              0.0,
              `Wrong side: expected ${expectedSide}, found ${detectedSide}`,
            );
          }
        } else {
          return CheckResult.success(1.0, `Name matches: ${matchedTerms[0]}`);
        }
      }
    }

    if (this.hints.length > 0) {
      const matchedHints = this.hints.filter((h) =>
        nameLower.includes(h.toLowerCase()),
      );
      if (matchedHints.length > 0) {
        let display = matchedHints.slice(0, 2).join(', ');
        if (matchedHints.length > 2) display += ` (+${matchedHints.length - 2})`;
        return CheckResult.success(1.0, `Name matches: ${display}`);
      }
    }

    return CheckResult.failure(0.0, 'No naming match');
  }

  private _detectSide(ctx: CheckContext, _nameLower: string): string | null {
    if (ctx.sideConvention && sideConventionIsValid(ctx.sideConvention)) {
      return sideConventionDetectSide(ctx.sideConvention, ctxBoneName(ctx));
    }
    return detectSideFromName(ctxBoneName(ctx));
  }
}

// ═══════════════════════════════════════════════════════════════════════
// TOPOLOGY CHECKS
// ═══════════════════════════════════════════════════════════════════════

export class ChildCountCheck extends Check {
  expected: [number, number];

  constructor(opts: {
    expected?: [number, number];
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'child_count',
      name: 'Child count',
      category: 'topology',
      weight: opts.weight ?? 0.3,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.expected = opts.expected ?? [0, 2];
  }

  evaluate(ctx: CheckContext): CheckResult {
    const bone = ctxBone(ctx);
    const actual = bone.children.filter(
      (c) => !ctx.skeleton.bone(c).auxiliary,
    ).length;
    const [minC, maxC] = this.expected;

    if (actual >= minC && actual <= maxC) {
      return CheckResult.success(
        1.0,
        `Children: ${actual} (expected ${minC}-${maxC})`,
      );
    }
    const distance = Math.min(Math.abs(actual - minC), Math.abs(actual - maxC));
    const score = clamp(1.0 - distance * 0.2);
    return CheckResult.neutral(
      score,
      `Children: ${actual} (expected ${minC}-${maxC})`,
    );
  }
}

export class DepthCheck extends Check {
  expected: [number, number];
  relativeTo: string | null;

  constructor(opts: {
    expected?: [number, number];
    relativeTo?: string | null;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'depth',
      name: 'Hierarchy depth',
      category: 'topology',
      weight: opts.weight ?? 0.3,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.expected = opts.expected ?? [0, 10];
    this.relativeTo = opts.relativeTo ?? null;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const actual = ctxBone(ctx).depth;
    let referenceDepth = 0;
    let referenceName = 'root';

    if (this.relativeTo && ctx.roleToBone) {
      const refBoneRef = ctx.roleToBone[this.relativeTo];
      if (refBoneRef) {
        const refIdx = ctx.skeleton.resolveBoneRef(refBoneRef, {
          namespace: 'internal',
        });
        if (refIdx != null) {
          referenceDepth = ctx.skeleton.bone(refIdx).depth;
          referenceName = this.relativeTo;
        }
      }
    }

    const relativeDepth = actual - referenceDepth;
    const [minD, maxD] = this.expected;

    if (relativeDepth >= minD && relativeDepth <= maxD) {
      return CheckResult.success(
        1.0,
        `Depth: ${relativeDepth} from ${referenceName} (expected ${minD}-${maxD})`,
      );
    }
    const distance = Math.min(
      Math.abs(relativeDepth - minD),
      Math.abs(relativeDepth - maxD),
    );
    const score = clamp(1.0 - distance / 4.0);
    return CheckResult.neutral(
      score,
      `Depth: ${relativeDepth} from ${referenceName} (expected ${minD}-${maxD})`,
    );
  }
}

export class ParentRoleCheck extends Check {
  expectedParentRole: string | null;

  constructor(opts: {
    expectedParentRole?: string | null;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'parent_role',
      name: 'Parent has expected role',
      category: 'topology',
      weight: opts.weight ?? 0.2,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.expectedParentRole = opts.expectedParentRole ?? null;
  }

  evaluate(ctx: CheckContext): CheckResult {
    if (this.expectedParentRole == null) {
      return CheckResult.neutral(0.5, 'No expected parent role defined');
    }

    const roleToBone = ctx.roleToBone ?? {};
    const expectedBoneRef = roleToBone[this.expectedParentRole];
    if (expectedBoneRef == null) {
      return CheckResult.neutral(
        0.5,
        `Parent role ${this.expectedParentRole} not assigned`,
      );
    }

    const expectedBoneIdx = ctx.skeleton.resolveBoneRef(expectedBoneRef, {
      namespace: 'internal',
    });
    if (expectedBoneIdx == null) {
      return CheckResult.neutral(
        0.5,
        `Parent role ${this.expectedParentRole} could not be resolved`,
      );
    }

    const bone = ctxBone(ctx);
    if (bone.parent == null) {
      return CheckResult.failure(0.0, 'Bone has no parent');
    }

    if (bone.parent === expectedBoneIdx) {
      return CheckResult.success(
        1.0,
        `Parent: ${ctx.skeleton.bone(bone.parent).name}`,
      );
    }

    const ancestors = ctx.skeleton.ancestors(ctx.boneIdx);
    for (const ancIdx of ancestors) {
      if (ancIdx === expectedBoneIdx) {
        return CheckResult.neutral(
          0.7,
          `Parent: ${ctx.skeleton.bone(bone.parent).name} (ancestor of expected)`,
        );
      }
    }

    return CheckResult.failure(
      0.3,
      `Parent: ${ctx.skeleton.bone(bone.parent).name} (expected ${this.expectedParentRole})`,
    );
  }
}

export class LinearChainCheck extends Check {
  constructor(opts: {
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'linear_chain',
      name: 'Linear chain',
      category: 'topology',
      weight: opts.weight ?? 0.2,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
  }

  evaluate(ctx: CheckContext): CheckResult {
    const bone = ctxBone(ctx);
    const childCount = bone.children.filter(
      (c) => !ctx.skeleton.bone(c).auxiliary,
    ).length;

    if (childCount === 1) {
      return CheckResult.success(1.0, 'Linear chain (1 child)');
    }
    if (childCount === 0) {
      return CheckResult.neutral(0.8, 'Chain endpoint (0 children)');
    }
    return CheckResult.neutral(0.5, `Branching (${childCount} children)`);
  }
}

export class ChainPositionCheck extends Check {
  expectedPosition: number | null;

  constructor(opts: {
    expectedPosition?: number | null;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'chain_position',
      name: 'Chain position',
      category: 'topology',
      weight: opts.weight ?? 0.2,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.expectedPosition = opts.expectedPosition ?? null;
  }

  evaluate(ctx: CheckContext): CheckResult {
    if (ctx.chainPosition == null || ctx.chainLength == null) {
      return CheckResult.neutral(0.5, 'Chain position unknown');
    }

    const pos = ctx.chainPosition;
    const total = ctx.chainLength;

    if (this.expectedPosition != null) {
      if (pos === this.expectedPosition) {
        return CheckResult.success(1.0, `Chain position: ${pos}/${total}`);
      }
      const distance = Math.abs(pos - this.expectedPosition);
      const score = clamp(1.0 - distance * 0.3);
      return CheckResult.neutral(
        score,
        `Chain position: ${pos}/${total} (expected ${this.expectedPosition})`,
      );
    }
    return CheckResult.neutral(0.5, `Chain position: ${pos}/${total}`);
  }
}

// ═══════════════════════════════════════════════════════════════════════
// GEOMETRY CHECKS
// ═══════════════════════════════════════════════════════════════════════

function describeDirection(vec: Vector3): string {
  const norm = vec.length();
  if (norm < 1e-8) return 'zero';
  const v = vec.clone().divideScalar(norm);
  const absX = Math.abs(v.x);
  const absY = Math.abs(v.y);
  const absZ = Math.abs(v.z);

  const positiveNames = ['+X (right)', '+Y (up)', '+Z (forward)'];
  const negativeNames = ['-X (left)', '-Y (down)', '-Z (back)'];
  const components = [absX, absY, absZ];
  const values = [v.x, v.y, v.z];

  let maxIdx = 0;
  for (let i = 1; i < 3; i++) {
    if (components[i] > components[maxIdx]) maxIdx = i;
  }
  return values[maxIdx] >= 0 ? positiveNames[maxIdx] : negativeNames[maxIdx];
}

export class DirectionCheck extends Check {
  expected: Vector3 | null;
  allowOpposite: boolean;

  constructor(opts: {
    expected?: Vector3 | null;
    allowOpposite?: boolean;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'direction',
      name: 'Direction alignment',
      category: 'geometry',
      weight: opts.weight ?? 0.4,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.expected = opts.expected ?? null;
    this.allowOpposite = opts.allowOpposite ?? false;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const expected = this.expected ?? ctx.expectedDirection ?? null;
    if (expected == null) {
      return CheckResult.neutral(0.5, 'No expected direction');
    }

    const boneDir = ctxBone(ctx).direction;
    const normA = boneDir.length();
    const normB = expected.length();

    if (normA < 1e-8 || normB < 1e-8) {
      return CheckResult.neutral(0.5, 'Zero-length direction vector');
    }

    let dot =
      boneDir.clone().divideScalar(normA).dot(expected.clone().divideScalar(normB));

    if (this.allowOpposite) {
      dot = Math.abs(dot);
    }

    const score = clamp((dot + 1.0) / 2.0);

    let dirDesc = describeDirection(expected);
    if (this.allowOpposite) dirDesc = `${dirDesc} or opposite`;

    const pct = Math.round(score * 100);
    if (score >= 0.7)
      return CheckResult.success(score, `Direction: ${pct}% aligned (${dirDesc})`);
    if (score < 0.5)
      return CheckResult.failure(score, `Direction: ${pct}% aligned (${dirDesc})`);
    return CheckResult.neutral(score, `Direction: ${pct}% aligned (${dirDesc})`);
  }
}

export class OutwardDirectionCheck extends Check {
  expectedSide: string;
  minLateralComponent: number;

  constructor(opts: {
    expectedSide?: string;
    minLateralComponent?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'outward_direction',
      name: 'Outward direction',
      category: 'geometry',
      weight: opts.weight ?? 0.3,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.expectedSide = opts.expectedSide ?? 'Left';
    this.minLateralComponent = opts.minLateralComponent ?? 0.1;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const boneDir = ctxBone(ctx).direction;
    const norm = boneDir.length();

    if (norm < 1e-8) {
      return CheckResult.neutral(0.5, 'Zero-length direction');
    }

    const normalized = boneDir.clone().divideScalar(norm);
    const latAxis = ctxLateralAxis(ctx);
    const lateral =
      latAxis === 0 ? normalized.x : latAxis === 1 ? normalized.y : normalized.z;

    const expectedSign = this.expectedSide === 'Left' ? -1.0 : 1.0;
    const lateralAlignment = lateral * expectedSign;

    if (Math.abs(lateral) < this.minLateralComponent) {
      return CheckResult.neutral(
        0.5,
        `Minimal lateral spread (${Math.abs(lateral).toFixed(2)})`,
      );
    }

    if (lateralAlignment > 0) {
      const score = 0.6 + 0.4 * Math.min(Math.abs(lateral), 1.0);
      return CheckResult.success(
        score,
        `Points ${this.expectedSide.toLowerCase()}ward`,
      );
    }
    const score = 0.3 - 0.2 * Math.min(Math.abs(lateral), 1.0);
    return CheckResult.failure(
      score,
      `Points opposite to ${this.expectedSide.toLowerCase()}`,
    );
  }
}

export class SymmetryCheck extends Check {
  constructor(opts: {
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'symmetry',
      name: 'Symmetry with mirror',
      category: 'geometry',
      weight: opts.weight ?? 0.4,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
  }

  evaluate(ctx: CheckContext): CheckResult {
    const chain = ctx.chain;
    const mirror = ctx.mirrorChain;

    if (!chain || !mirror) {
      return CheckResult.neutral(0.5, 'No mirror chain for comparison');
    }
    if (chain.length === 0 || mirror.length === 0) {
      return CheckResult.neutral(0.5, 'Empty chain');
    }

    const skel = ctx.skeleton;
    const lenRatio = Math.min(chain.length, mirror.length) / Math.max(chain.length, mirror.length);

    const chainLen = (indices: number[]): number =>
      indices.reduce((sum, i) => sum + skel.bone(i).length, 0);

    const lenA = chainLen(chain);
    const lenB = chainLen(mirror);
    const maxLen = Math.max(lenA, lenB);
    const boneLenRatio = maxLen > 1e-8 ? Math.min(lenA, lenB) / maxLen : 0.0;

    const depthA = skel.bone(chain[0]).depth;
    const depthB = skel.bone(mirror[0]).depth;
    const depthScore = 1.0 - Math.min(Math.abs(depthA - depthB) / 3.0, 1.0);

    const score = clamp(0.4 * lenRatio + 0.35 * boneLenRatio + 0.25 * depthScore);
    const pct = Math.round(score * 100);
    if (score >= 0.7) return CheckResult.success(score, `Symmetry: ${pct}%`);
    if (score < 0.5) return CheckResult.failure(score, `Symmetry: ${pct}%`);
    return CheckResult.neutral(score, `Symmetry: ${pct}%`);
  }
}

export class LengthRatioCheck extends Check {
  tolerance: number;

  constructor(opts: {
    tolerance?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'length_ratio',
      name: 'Length ratio',
      category: 'geometry',
      weight: opts.weight ?? 0.2,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.tolerance = opts.tolerance ?? 0.4;
  }

  evaluate(ctx: CheckContext): CheckResult {
    if (!ctx.mirrorChain || ctx.mirrorChain.length === 0) {
      return CheckResult.neutral(0.5, 'No reference for length comparison');
    }
    if (ctx.chainPosition == null || ctx.chainPosition <= 0) {
      return CheckResult.neutral(0.5, 'Chain position unknown');
    }

    const pos = ctx.chainPosition - 1;
    if (pos >= ctx.mirrorChain.length) {
      return CheckResult.neutral(0.5, 'No mirror bone at this position');
    }

    const lenA = ctxBone(ctx).length;
    const lenB = ctx.skeleton.bone(ctx.mirrorChain[pos]).length;
    const maxLen = Math.max(lenA, lenB);

    if (maxLen <= 1e-8) {
      return CheckResult.neutral(0.5, 'Zero-length bones');
    }

    const ratio = Math.min(lenA, lenB) / maxLen;
    const score = clamp((ratio - this.tolerance) / (1.0 - this.tolerance));
    const pct = Math.round(ratio * 100);

    if (score >= 0.7) return CheckResult.success(score, `Length ratio: ${pct}%`);
    if (score < 0.5) return CheckResult.failure(score, `Length ratio: ${pct}%`);
    return CheckResult.neutral(score, `Length ratio: ${pct}%`);
  }
}

export class BendCheck extends Check {
  minBendAngle: number;

  constructor(opts: {
    minBendAngle?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'bend',
      name: 'Joint bend detection',
      category: 'geometry',
      weight: opts.weight ?? 0.3,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.minBendAngle = opts.minBendAngle ?? 15.0;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const bone = ctxBone(ctx);
    const skel = ctx.skeleton;

    if (bone.parent == null) {
      return CheckResult.neutral(0.5, 'No parent bone');
    }

    const parent = skel.bone(bone.parent);
    const boneDir = bone.direction;
    const parentDir = parent.direction;
    const normBone = boneDir.length();
    const normParent = parentDir.length();

    if (normBone < 1e-8 || normParent < 1e-8) {
      return CheckResult.neutral(0.5, 'Zero-length direction vector');
    }

    const normBoneDir = boneDir.clone().divideScalar(normBone);
    const normParentDir = parentDir.clone().divideScalar(normParent);
    const dot = normBoneDir.dot(normParentDir);

    const angle =
      (Math.acos(Math.max(-1.0, Math.min(1.0, dot))) * 180.0) / Math.PI;

    if (angle < this.minBendAngle) {
      const score = 0.2 + 0.3 * (angle / this.minBendAngle);
      return CheckResult.failure(
        score,
        `Inline with parent (${Math.round(angle)}° bend)`,
      );
    }
    if (angle < 45) {
      const score =
        0.5 +
        0.3 * ((angle - this.minBendAngle) / (45 - this.minBendAngle));
      return CheckResult.neutral(score, `Moderate bend (${Math.round(angle)}°)`);
    }
    const score = 0.8 + 0.2 * Math.min((angle - 45) / 45, 1.0);
    return CheckResult.success(score, `Clear bend (${Math.round(angle)}°)`);
  }
}

// ─── Spatial checks ─────────────────────────────────────────────────────

export class VerticalPositionCheck extends Check {
  expectedMin: number;
  expectedMax: number;

  constructor(opts: {
    expectedMin?: number;
    expectedMax?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'vertical_position',
      name: 'Vertical position',
      category: 'spatial',
      weight: opts.weight ?? 0.3,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.expectedMin = opts.expectedMin ?? 0.0;
    this.expectedMax = opts.expectedMax ?? 1.0;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const spatialData = ctxExtra(ctx).spatial_data as SpatialData | undefined;
    if (!spatialData) {
      return CheckResult.neutral(0.5, 'No spatial data available');
    }

    const verticalPos = spatialData.getVerticalPosition(ctx.boneIdx);
    const minPct = Math.round(this.expectedMin * 100);
    const maxPct = Math.round(this.expectedMax * 100);
    const posPct = Math.round(verticalPos * 100);

    if (verticalPos >= this.expectedMin && verticalPos <= this.expectedMax) {
      const rangeSize = this.expectedMax - this.expectedMin;
      let score: number;
      if (rangeSize > 0) {
        const center = (this.expectedMin + this.expectedMax) / 2;
        const distFromCenter = Math.abs(verticalPos - center) / (rangeSize / 2);
        score = 1.0 - 0.3 * distFromCenter;
      } else {
        score = 1.0;
      }
      return CheckResult.success(
        score,
        `Vertical position: ${posPct}% (expected ${minPct}-${maxPct}%)`,
      );
    }

    const distance =
      verticalPos < this.expectedMin
        ? this.expectedMin - verticalPos
        : verticalPos - this.expectedMax;
    const score = Math.max(0.0, 0.5 - distance);
    return CheckResult.failure(
      score,
      `Vertical position: ${posPct}% (expected ${minPct}-${maxPct}%)`,
    );
  }
}

export class SymmetricChildrenCheck extends Check {
  expectSymmetric: boolean;
  expectDownward: boolean;
  expectLateral: boolean;
  expectUpward: boolean;
  minChildren: number;

  constructor(opts: {
    expectSymmetric?: boolean;
    expectDownward?: boolean;
    expectLateral?: boolean;
    expectUpward?: boolean;
    minChildren?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'symmetric_children',
      name: 'Symmetric children',
      category: 'spatial',
      weight: opts.weight ?? 0.4,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.expectSymmetric = opts.expectSymmetric ?? true;
    this.expectDownward = opts.expectDownward ?? false;
    this.expectLateral = opts.expectLateral ?? false;
    this.expectUpward = opts.expectUpward ?? false;
    this.minChildren = opts.minChildren ?? 2;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const spatialData = ctxExtra(ctx).spatial_data as SpatialData | undefined;
    if (!spatialData) {
      return CheckResult.neutral(0.5, 'No spatial data available');
    }

    const bp = spatialData.getBranchingPoint(ctx.boneIdx);
    if (!bp) {
      return CheckResult.neutral(0.5, 'Not a branching point');
    }

    if (bp.childCount < this.minChildren) {
      return CheckResult.failure(
        0.3,
        `Only ${bp.childCount} children (need ${this.minChildren}+)`,
      );
    }

    let score = 0.5;
    const matches: string[] = [];
    const misses: string[] = [];

    if (this.expectSymmetric) {
      if (bp.hasSymmetricChildren) {
        score += 0.2;
        matches.push('symmetric');
      } else {
        score -= 0.1;
        misses.push('not symmetric');
      }
    }
    if (this.expectDownward) {
      if (bp.hasDownwardChildren) {
        score += 0.15;
        matches.push('downward');
      } else {
        score -= 0.1;
        misses.push('no downward');
      }
    }
    if (this.expectLateral) {
      if (bp.hasLateralChildren) {
        score += 0.15;
        matches.push('lateral');
      } else {
        score -= 0.1;
        misses.push('no lateral');
      }
    }
    if (this.expectUpward) {
      if (bp.hasUpwardChildren) {
        score += 0.1;
        matches.push('upward');
      } else {
        score -= 0.05;
        misses.push('no upward');
      }
    }

    score = clamp(score);

    if (matches.length > 0 && misses.length === 0) {
      return CheckResult.success(score, `Branching: ${matches.join(', ')}`);
    }
    if (misses.length > 0 && matches.length === 0) {
      return CheckResult.failure(score, `Branching: ${misses.join(', ')}`);
    }
    return CheckResult.neutral(
      score,
      `Branching: ${[...matches, ...misses].join(', ')}`,
    );
  }
}

export class SubtreePatternCheck extends Check {
  minLeaves: number;
  maxLeaves: number;
  minDepth: number;
  maxDepth: number;
  minSubtreeSize: number;
  maxSubtreeSize: number;

  constructor(opts: {
    minLeaves?: number;
    maxLeaves?: number;
    minDepth?: number;
    maxDepth?: number;
    minSubtreeSize?: number;
    maxSubtreeSize?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'subtree_pattern',
      name: 'Subtree pattern',
      category: 'spatial',
      weight: opts.weight ?? 0.3,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.minLeaves = opts.minLeaves ?? 0;
    this.maxLeaves = opts.maxLeaves ?? 100;
    this.minDepth = opts.minDepth ?? 0;
    this.maxDepth = opts.maxDepth ?? 20;
    this.minSubtreeSize = opts.minSubtreeSize ?? 0;
    this.maxSubtreeSize = opts.maxSubtreeSize ?? 200;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const skel = ctx.skeleton;
    let subtreeDepth = 0;

    const countLeaves = (idx: number, depth: number): number => {
      subtreeDepth = Math.max(subtreeDepth, depth);
      const b = skel.bone(idx);
      const nonAuxChildren = b.children.filter((c) => !skel.bone(c).auxiliary);
      if (nonAuxChildren.length === 0) return 1;
      return nonAuxChildren.reduce(
        (sum, c) => sum + countLeaves(c, depth + 1),
        0,
      );
    };

    const leafCount = countLeaves(ctx.boneIdx, 0);
    const subtreeSize = ctxBone(ctx).subtreeSize;

    let matches = 0;
    let total = 0;

    if (this.minLeaves > 0 || this.maxLeaves < 100) {
      total++;
      if (leafCount >= this.minLeaves && leafCount <= this.maxLeaves) matches++;
    }
    if (this.minDepth > 0 || this.maxDepth < 20) {
      total++;
      if (subtreeDepth >= this.minDepth && subtreeDepth <= this.maxDepth) matches++;
    }
    if (this.minSubtreeSize > 0 || this.maxSubtreeSize < 200) {
      total++;
      if (subtreeSize >= this.minSubtreeSize && subtreeSize <= this.maxSubtreeSize)
        matches++;
    }

    if (total === 0) {
      return CheckResult.neutral(0.5, 'No subtree criteria specified');
    }

    const score = matches / total;
    const reason = `Subtree: ${leafCount} leaves, depth ${subtreeDepth}, size ${subtreeSize}`;

    if (score >= 0.7) return CheckResult.success(score, reason);
    if (score < 0.5) return CheckResult.failure(score, reason);
    return CheckResult.neutral(score, reason);
  }
}

export class RelativeVerticalCheck extends Check {
  referenceRole: string;
  expectedRelation: 'below' | 'above';
  minDifference: number;

  constructor(opts: {
    referenceRole?: string;
    expectedRelation?: 'below' | 'above';
    minDifference?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'relative_vertical',
      name: 'Relative vertical position',
      category: 'spatial',
      weight: opts.weight ?? 0.25,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.referenceRole = opts.referenceRole ?? '';
    this.expectedRelation = opts.expectedRelation ?? 'below';
    this.minDifference = opts.minDifference ?? 0.02;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const spatialData = ctxExtra(ctx).spatial_data as SpatialData | undefined;
    if (!spatialData) return CheckResult.neutral(0.5, 'No spatial data available');
    if (!this.referenceRole)
      return CheckResult.neutral(0.5, 'No reference role specified');

    let refBoneIdx: number | null = null;

    const mapping = ctxExtra(ctx).mapping as Record<string, number> | undefined;
    if (mapping && this.referenceRole in mapping) {
      refBoneIdx = mapping[this.referenceRole];
    }

    if (refBoneIdx == null && ctx.roleToBone) {
      const refBoneRef = ctx.roleToBone[this.referenceRole];
      if (refBoneRef) {
        refBoneIdx = ctx.skeleton.resolveBoneRef(refBoneRef, {
          namespace: 'internal',
        });
      }
    }

    if (refBoneIdx == null) {
      return CheckResult.neutral(
        0.5,
        `Reference '${this.referenceRole}' not mapped`,
      );
    }

    const bonePos = spatialData.getVerticalPosition(ctx.boneIdx);
    const refPos = spatialData.getVerticalPosition(refBoneIdx);
    const diff = refPos - bonePos;

    if (this.expectedRelation === 'below') {
      if (diff >= this.minDifference) {
        const score = Math.min(1.0, 0.7 + diff);
        return CheckResult.success(
          score,
          `Below ${this.referenceRole} by ${Math.round(diff * 100)}%`,
        );
      }
      if (diff > -this.minDifference) {
        return CheckResult.neutral(
          0.5,
          `At same level as ${this.referenceRole}`,
        );
      }
      const score = Math.max(0.0, 0.4 + diff);
      return CheckResult.failure(
        score,
        `Above ${this.referenceRole} by ${Math.round(-diff * 100)}%`,
      );
    }

    if (diff <= -this.minDifference) {
      const score = Math.min(1.0, 0.7 + Math.abs(diff));
      return CheckResult.success(
        score,
        `Above ${this.referenceRole} by ${Math.round(-diff * 100)}%`,
      );
    }
    if (diff < this.minDifference) {
      return CheckResult.neutral(
        0.5,
        `At same level as ${this.referenceRole}`,
      );
    }
    const score = Math.max(0.0, 0.4 - diff);
    return CheckResult.failure(
      score,
      `Below ${this.referenceRole} by ${Math.round(diff * 100)}%`,
    );
  }
}

export class AnchorRelativePositionCheck extends Check {
  anchorRole: string;
  expectedMin: number;
  expectedMax: number;

  constructor(opts: {
    anchorRole?: string;
    expectedMin?: number;
    expectedMax?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'anchor_relative_position',
      name: 'Anchor-relative position',
      category: 'spatial',
      weight: opts.weight ?? 0.25,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.anchorRole = opts.anchorRole ?? '';
    this.expectedMin = opts.expectedMin ?? -1.0;
    this.expectedMax = opts.expectedMax ?? 1.0;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const spatialData = ctxExtra(ctx).spatial_data as SpatialData | undefined;
    if (!spatialData) return CheckResult.neutral(0.5, 'No spatial data available');
    if (!this.anchorRole) return CheckResult.neutral(0.5, 'No anchor role specified');

    let anchorIdx: number | null = null;

    const mapping = ctxExtra(ctx).mapping as Record<string, number> | undefined;
    if (mapping && this.anchorRole in mapping) {
      anchorIdx = mapping[this.anchorRole];
    }

    if (anchorIdx == null && ctx.roleToBone) {
      const anchorBoneRef = ctx.roleToBone[this.anchorRole];
      if (anchorBoneRef) {
        anchorIdx = ctx.skeleton.resolveBoneRef(anchorBoneRef, {
          namespace: 'internal',
        });
      }
    }

    if (anchorIdx == null) {
      return CheckResult.neutral(
        0.5,
        `Anchor '${this.anchorRole}' not mapped`,
      );
    }

    const bonePos = spatialData.getVerticalPosition(ctx.boneIdx);
    const anchorPos = spatialData.getVerticalPosition(anchorIdx);
    const offset = bonePos - anchorPos;
    const pct = Math.round(offset * 100);
    const sign = pct >= 0 ? '+' : '';
    const expMinPct = Math.round(this.expectedMin * 100);
    const expMaxPct = Math.round(this.expectedMax * 100);

    if (offset >= this.expectedMin && offset <= this.expectedMax) {
      const rangeSize = this.expectedMax - this.expectedMin;
      let score: number;
      if (rangeSize > 0) {
        const center = (this.expectedMin + this.expectedMax) / 2;
        const distFromCenter = Math.abs(offset - center) / (rangeSize / 2);
        score = 1.0 - 0.3 * distFromCenter;
      } else {
        score = 1.0;
      }
      return CheckResult.success(
        score,
        `${sign}${pct}% from ${this.anchorRole} (expected ${expMinPct} to ${expMaxPct}%)`,
      );
    }

    const distance =
      offset < this.expectedMin
        ? this.expectedMin - offset
        : offset - this.expectedMax;
    const score = Math.max(0.0, 0.5 - distance);
    return CheckResult.failure(
      score,
      `${sign}${pct}% from ${this.anchorRole} (expected ${expMinPct} to ${expMaxPct}%)`,
    );
  }
}

// ═══════════════════════════════════════════════════════════════════════
// CONSTRAINT CHECKS
// ═══════════════════════════════════════════════════════════════════════

export class LateralSpreadCheck extends Check {
  minSpread: number;

  constructor(opts: {
    minSpread?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'lateral_spread',
      name: 'Lateral spread',
      category: 'constraint',
      weight: opts.weight ?? 1.0,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.minSpread = opts.minSpread ?? 0.5;
  }

  evaluate(ctx: CheckContext): CheckResult {
    if (!ctx.mirrorChain || ctx.mirrorChain.length === 0) {
      return CheckResult.neutral(0.5, 'No mirror chain for spread check');
    }
    if (!ctx.chain || ctx.chain.length === 0) {
      return CheckResult.neutral(0.5, 'No chain for spread check');
    }

    const skel = ctx.skeleton;
    const posA = skel.getBoneWorldPosition(ctx.chain[0]);
    const posB = skel.getBoneWorldPosition(ctx.mirrorChain[0]);

    const firstBone = skel.bone(ctx.chain[0]);
    const referencePos =
      firstBone.parent != null
        ? skel.getBoneWorldPosition(firstBone.parent)
        : new Vector3();

    const latAxis = ctxLateralAxis(ctx);
    const lateralVec = new Vector3();
    if (latAxis === 0) lateralVec.x = 1;
    else if (latAxis === 1) lateralVec.y = 1;
    else lateralVec.z = 1;

    const diffA = posA.clone().sub(referencePos);
    const diffB = posB.clone().sub(referencePos);
    const offsetA = diffA.dot(lateralVec);
    const offsetB = diffB.dot(lateralVec);

    if (offsetA * offsetB >= 0) {
      return CheckResult.failure(0.3, 'Limbs on same side of center');
    }

    const spread = Math.abs(offsetA - offsetB);
    if (spread < this.minSpread) {
      const score = clamp(0.3 + 0.7 * (spread / this.minSpread));
      return CheckResult.neutral(
        score,
        `Lateral spread: ${Math.round(score * 100)}%`,
      );
    }

    return CheckResult.success(1.0, 'Lateral spread: 100%');
  }
}

export class CenterAlignmentCheck extends Check {
  tolerance: number;

  constructor(opts: {
    tolerance?: number;
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'center_alignment',
      name: 'Center alignment',
      category: 'constraint',
      weight: opts.weight ?? 1.0,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
    this.tolerance = opts.tolerance ?? 1.0;
  }

  evaluate(ctx: CheckContext): CheckResult {
    const skel = ctx.skeleton;
    const position = skel.getBoneWorldPosition(ctx.boneIdx);
    const rootPos = skel.getBoneWorldPosition(skel.root);

    const latAxis = ctxLateralAxis(ctx);
    const lateralVec = new Vector3();
    if (latAxis === 0) lateralVec.x = 1;
    else if (latAxis === 1) lateralVec.y = 1;
    else lateralVec.z = 1;

    const diff = position.clone().sub(rootPos);
    const offset = Math.abs(diff.dot(lateralVec));

    if (offset <= this.tolerance) {
      return CheckResult.success(1.0, 'Center alignment: 100%');
    }

    const score = clamp(
      1.0 - (offset - this.tolerance) / (this.tolerance * 3.0),
    );
    const pct = Math.round(score * 100);

    if (score >= 0.7) return CheckResult.success(score, `Center alignment: ${pct}%`);
    if (score < 0.5) return CheckResult.failure(score, `Center alignment: ${pct}%`);
    return CheckResult.neutral(score, `Center alignment: ${pct}%`);
  }
}

export class ToeParentCheck extends Check {
  constructor(opts: {
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'toe_parent',
      name: 'Toe parent',
      category: 'constraint',
      weight: opts.weight ?? 0.5,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
  }

  evaluate(ctx: CheckContext): CheckResult {
    const bone = ctxBone(ctx);
    if (bone.parent == null) {
      return CheckResult.failure(0.0, 'Toe has no parent');
    }

    const parent = ctx.skeleton.bone(bone.parent);
    switch (classifyToeParentName(parent.name)) {
      case 'foot':
        return CheckResult.success(
          1.0,
          `Direct child of foot (${parent.name})`,
        );
      case 'leg':
        return CheckResult.neutral(0.6, `Child of leg bone (${parent.name})`);
      case 'toe':
        return CheckResult.failure(0.3, `Child of toe bone (${parent.name})`);
      default:
        return CheckResult.neutral(0.5, `Parent: ${parent.name}`);
    }
  }
}

export class ToeDirectionCheck extends Check {
  constructor(opts: {
    weight?: number;
    required?: boolean;
    weightKey?: string | null;
  } = {}) {
    super({
      id: 'toe_direction',
      name: 'Toe direction',
      category: 'constraint',
      weight: opts.weight ?? 0.5,
      required: opts.required ?? false,
      weightKey: opts.weightKey ?? null,
    });
  }

  evaluate(ctx: CheckContext): CheckResult {
    const bone = ctxBone(ctx);
    const toeDir = bone.direction;
    const toeNorm = toeDir.length();

    if (toeNorm < 1e-8) {
      return CheckResult.neutral(0.5, 'Zero-length toe');
    }

    const normToe = toeDir.clone().divideScalar(toeNorm);

    const footDir = ctxExtra(ctx).foot_direction as Vector3 | undefined;
    if (footDir) {
      const footNorm = footDir.length();
      if (footNorm > 1e-8) {
        const normFoot = footDir.clone().divideScalar(footNorm);
        const footDot = normToe.dot(normFoot);
        if (footDot > 0.5)
          return CheckResult.success(1.0, 'Toe aligns with foot direction');
        if (footDot > 0)
          return CheckResult.neutral(0.7, 'Toe partially aligned with foot');
        if (footDot > -0.5)
          return CheckResult.neutral(0.4, 'Toe perpendicular to foot');
        return CheckResult.failure(0.2, 'Toe points backward');
      }
    }

    const legDir = ctx.expectedDirection ?? null;
    if (legDir) {
      const legNorm = legDir.length();
      if (legNorm > 1e-8) {
        const normLeg = legDir.clone().divideScalar(legNorm);
        const legDot = Math.abs(normToe.dot(normLeg));
        if (legDot < 0.3)
          return CheckResult.success(
            1.0,
            'Toe perpendicular to leg (good)',
          );
        if (legDot < 0.6)
          return CheckResult.neutral(0.7, 'Toe somewhat diagonal');
        return CheckResult.neutral(0.4, 'Toe parallel to leg');
      }
    }

    return CheckResult.neutral(0.5, 'Toe direction: unknown reference');
  }
}

// ═══════════════════════════════════════════════════════════════════════
// WEIGHT CONSTANTS
// ═══════════════════════════════════════════════════════════════════════

export const W_NAMING = 0.10;
export const W_NAMING_HIGH = 0.20;
export const W_TOPOLOGY = 0.15;
export const W_TOPOLOGY_HIGH = 0.25;
export const W_DIRECTION = 0.25;
export const W_SYMMETRY = 0.15;
export const W_CHAIN_POS = 0.10;
export const W_CONSTRAINT = 0.10;
export const W_CONSTRAINT_HIGH = 0.20;
export const W_SPATIAL = 0.20;
export const W_SPATIAL_HIGH = 0.30;
export const W_SPATIAL_LIMB = 0.10;
export const W_SPATIAL_REL = 0.15;

// ═══════════════════════════════════════════════════════════════════════
// DIRECTION VECTORS
// ═══════════════════════════════════════════════════════════════════════

export const DOWN = new Vector3(0, -1, 0);
export const UP = new Vector3(0, 1, 0);
export const FORWARD = new Vector3(0, 0, 1);
export const BACKWARD = new Vector3(0, 0, -1);
export const LEFT = new Vector3(-1, 0, 0);
export const RIGHT = new Vector3(1, 0, 0);

// ═══════════════════════════════════════════════════════════════════════
// CATEGORIES
// ═══════════════════════════════════════════════════════════════════════

export const CATEGORIES: string[] = [
  'naming',
  'topology',
  'geometry',
  'spatial',
  'constraint',
];

export const CATEGORY_WEIGHTS: Record<string, number> = Object.fromEntries(
  CATEGORIES.map((c) => [c, 1.0]),
);

// ═══════════════════════════════════════════════════════════════════════
// REGISTRY HELPERS
// ═══════════════════════════════════════════════════════════════════════

function _getHints(role: string): string[] {
  return ROLE_HINTS[role] ?? [];
}

function _getChildRange(role: string): [number, number] {
  return EXPECTED_CHILD_COUNTS[role] ?? [0, 5];
}

function _getDepthRange(role: string): [number, number] {
  return EXPECTED_DEPTHS[role] ?? [0, 15];
}

function _baseChecks(
  role: string,
  namingWeight = W_NAMING,
  namingKey = 'naming',
): Check[] {
  return [
    new NamingMatchCheck({
      hints: _getHints(role),
      weight: namingWeight,
      weightKey: namingKey,
    }),
    new ChildCountCheck({
      expected: _getChildRange(role),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange(role),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
  ];
}

function _limbChecks(
  role: string,
  expectedDirection: Vector3,
  position = 1,
  requiresBend = false,
  minBendAngle = 15.0,
): Check[] {
  const checks: Check[] = [
    new NamingMatchCheck({
      hints: _getHints(role),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange(role),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange(role),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DirectionCheck({
      expected: expectedDirection,
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new SymmetryCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new LengthRatioCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new LateralSpreadCheck({ weight: W_CONSTRAINT, weightKey: 'constraint' }),
    new ChainPositionCheck({
      expectedPosition: position,
      weight: W_CHAIN_POS,
      weightKey: 'chain_position',
    }),
  ];
  if (requiresBend) {
    checks.push(
      new BendCheck({
        weight: W_CONSTRAINT,
        weightKey: 'constraint',
        minBendAngle,
      }),
    );
  }
  return checks;
}

function _armChecks(
  role: string,
  side: string,
  position = 1,
  requiresBend = false,
  minBendAngle = 15.0,
): Check[] {
  const checks: Check[] = [
    new SideMatchCheck({
      expectedSide: side,
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints(role),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange(role),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange(role),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new OutwardDirectionCheck({
      expectedSide: side,
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new SymmetryCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new LengthRatioCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new LateralSpreadCheck({ weight: W_CONSTRAINT, weightKey: 'constraint' }),
    new ChainPositionCheck({
      expectedPosition: position,
      weight: W_CHAIN_POS,
      weightKey: 'chain_position',
    }),
    new RelativeVerticalCheck({
      referenceRole: 'Chest',
      expectedRelation: 'below',
      minDifference: 0.0,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];
  if (requiresBend) {
    checks.push(
      new BendCheck({
        weight: W_CONSTRAINT,
        weightKey: 'constraint',
        minBendAngle,
      }),
    );
  }
  return checks;
}

function _legChecks(
  role: string,
  side: string,
  expectedDirection: Vector3,
  position = 1,
  requiresBend = false,
  minBendAngle = 15.0,
): Check[] {
  const checks: Check[] = [
    new SideMatchCheck({
      expectedSide: side,
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints(role),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange(role),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: [position, position + 2] as [number, number],
      relativeTo: 'Pelvis',
      weight: W_TOPOLOGY_HIGH,
      weightKey: 'topology_high',
    }),
    new DirectionCheck({
      expected: expectedDirection,
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new SymmetryCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new LengthRatioCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new LateralSpreadCheck({ weight: W_CONSTRAINT, weightKey: 'constraint' }),
    new ChainPositionCheck({
      expectedPosition: position,
      weight: W_CHAIN_POS,
      weightKey: 'chain_position',
    }),
    new RelativeVerticalCheck({
      referenceRole: 'Pelvis',
      expectedRelation: 'below',
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];
  if (requiresBend) {
    checks.push(
      new BendCheck({
        weight: W_CONSTRAINT,
        weightKey: 'constraint',
        minBendAngle,
      }),
    );
  }
  return checks;
}

function _axialChecks(role: string, expectedDirection: Vector3 = UP): Check[] {
  return [
    new NamingMatchCheck({
      hints: _getHints(role),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange(role),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange(role),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DirectionCheck({
      expected: expectedDirection,
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new CenterAlignmentCheck({
      weight: W_CONSTRAINT_HIGH,
      weightKey: 'constraint_high',
    }),
    new LinearChainCheck({ weight: W_TOPOLOGY, weightKey: 'topology' }),
  ];
}

function _fingerChecks(role: string, position: number): Check[] {
  return [
    new NamingMatchCheck({
      hints: _getHints(role),
      weight: W_NAMING_HIGH,
      weightKey: 'naming_high',
    }),
    new DepthCheck({
      expected:
        role in EXPECTED_DEPTHS
          ? (EXPECTED_DEPTHS[role] as [number, number])
          : [8, 15],
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new ChainPositionCheck({
      expectedPosition: position,
      weight: W_CHAIN_POS,
      weightKey: 'chain_position',
    }),
    new LinearChainCheck({ weight: W_TOPOLOGY, weightKey: 'topology' }),
  ];
}

// ═══════════════════════════════════════════════════════════════════════
// ROLE CHECKS REGISTRY
// ═══════════════════════════════════════════════════════════════════════

export function buildRoleChecks(): Record<string, Check[]> {
  const checks: Record<string, Check[]> = {};

  // === CORE ===
  checks['Root'] = [
    new NamingMatchCheck({
      hints: _getHints('Root'),
      weight: W_NAMING_HIGH,
      weightKey: 'naming_high',
    }),
    new ChildCountCheck({
      expected: [1, 1],
      weight: W_TOPOLOGY_HIGH,
      weightKey: 'topology_high',
    }),
    new DepthCheck({
      expected: [0, 0],
      weight: W_TOPOLOGY_HIGH,
      weightKey: 'topology_high',
    }),
  ];

  checks['Pelvis'] = [
    new NamingMatchCheck({
      hints: _getHints('Pelvis'),
      weight: W_NAMING_HIGH,
      weightKey: 'naming_high',
    }),
    new ChildCountCheck({
      expected: _getChildRange('Pelvis'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange('Pelvis'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new CenterAlignmentCheck({
      weight: W_CONSTRAINT_HIGH,
      weightKey: 'constraint_high',
    }),
  ];

  // === SPINE ===
  checks['Spine'] = [
    ..._axialChecks('Spine', UP),
    new VerticalPositionCheck({
      expectedMin: 0.45,
      expectedMax: 0.7,
      weight: W_SPATIAL,
      weightKey: 'spatial',
    }),
  ];

  checks['Chest'] = [
    ..._axialChecks('Chest', UP),
    new VerticalPositionCheck({
      expectedMin: 0.6,
      expectedMax: 0.85,
      weight: W_SPATIAL,
      weightKey: 'spatial',
    }),
    new SymmetricChildrenCheck({
      expectSymmetric: true,
      expectLateral: true,
      expectUpward: true,
      minChildren: 2,
      weight: W_SPATIAL,
      weightKey: 'spatial',
    }),
  ];

  checks['Neck'] = _axialChecks('Neck', UP);

  checks['Head'] = [
    new NamingMatchCheck({
      hints: _getHints('Head'),
      weight: W_NAMING_HIGH,
      weightKey: 'naming_high',
    }),
    new ChildCountCheck({
      expected: _getChildRange('Head'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange('Head'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DirectionCheck({
      expected: UP,
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new CenterAlignmentCheck({
      weight: W_CONSTRAINT_HIGH,
      weightKey: 'constraint_high',
    }),
    new VerticalPositionCheck({
      expectedMin: 0.85,
      expectedMax: 1.0,
      weight: W_SPATIAL,
      weightKey: 'spatial',
    }),
  ];

  // === LEFT ARM ===
  checks['LeftClavicle'] = [
    new SideMatchCheck({
      expectedSide: 'Left',
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints('LeftClavicle'),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange('LeftClavicle'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange('LeftClavicle'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DirectionCheck({
      expected: LEFT,
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new SymmetryCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new VerticalPositionCheck({
      expectedMin: 0.55,
      expectedMax: 0.9,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
  ];

  checks['LeftUpperArm'] = [
    ..._armChecks('LeftUpperArm', 'Left', 1),
    new VerticalPositionCheck({
      expectedMin: 0.3,
      expectedMax: 0.9,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Chest',
      expectedMin: -0.35,
      expectedMax: 0.1,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['LeftLowerArm'] = [
    ..._armChecks('LeftLowerArm', 'Left', 2, true, 5.0),
    new VerticalPositionCheck({
      expectedMin: 0.1,
      expectedMax: 0.85,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Chest',
      expectedMin: -0.6,
      expectedMax: 0.05,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['LeftHand'] = [
    new SideMatchCheck({
      expectedSide: 'Left',
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints('LeftHand'),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange('LeftHand'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange('LeftHand'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new OutwardDirectionCheck({
      expectedSide: 'Left',
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new SymmetryCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new ChainPositionCheck({
      expectedPosition: 3,
      weight: W_CHAIN_POS,
      weightKey: 'chain_position',
    }),
    new VerticalPositionCheck({
      expectedMin: 0.0,
      expectedMax: 0.85,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Chest',
      expectedMin: -0.8,
      expectedMax: 0.1,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  // === RIGHT ARM ===
  checks['RightClavicle'] = [
    new SideMatchCheck({
      expectedSide: 'Right',
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints('RightClavicle'),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange('RightClavicle'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange('RightClavicle'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DirectionCheck({
      expected: RIGHT,
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new SymmetryCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new VerticalPositionCheck({
      expectedMin: 0.55,
      expectedMax: 0.9,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
  ];

  checks['RightUpperArm'] = [
    ..._armChecks('RightUpperArm', 'Right', 1),
    new VerticalPositionCheck({
      expectedMin: 0.3,
      expectedMax: 0.9,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Chest',
      expectedMin: -0.35,
      expectedMax: 0.1,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['RightLowerArm'] = [
    ..._armChecks('RightLowerArm', 'Right', 2, true, 5.0),
    new VerticalPositionCheck({
      expectedMin: 0.1,
      expectedMax: 0.85,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Chest',
      expectedMin: -0.6,
      expectedMax: 0.05,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['RightHand'] = [
    new SideMatchCheck({
      expectedSide: 'Right',
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints('RightHand'),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange('RightHand'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: _getDepthRange('RightHand'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new OutwardDirectionCheck({
      expectedSide: 'Right',
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new SymmetryCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new ChainPositionCheck({
      expectedPosition: 3,
      weight: W_CHAIN_POS,
      weightKey: 'chain_position',
    }),
    new VerticalPositionCheck({
      expectedMin: 0.0,
      expectedMax: 0.85,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Chest',
      expectedMin: -0.8,
      expectedMax: 0.1,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  // === LEFT LEG ===
  checks['LeftUpperLeg'] = [
    ..._legChecks('LeftUpperLeg', 'Left', DOWN, 1),
    new VerticalPositionCheck({
      expectedMin: 0.1,
      expectedMax: 0.65,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Pelvis',
      expectedMin: -0.35,
      expectedMax: 0.0,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['LeftLowerLeg'] = [
    ..._legChecks('LeftLowerLeg', 'Left', DOWN, 2, true, 5.0),
    new VerticalPositionCheck({
      expectedMin: 0.0,
      expectedMax: 0.45,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Pelvis',
      expectedMin: -0.5,
      expectedMax: -0.1,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['LeftFoot'] = [
    new SideMatchCheck({
      expectedSide: 'Left',
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints('LeftFoot'),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange('LeftFoot'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: [3, 5],
      relativeTo: 'Pelvis',
      weight: W_TOPOLOGY_HIGH,
      weightKey: 'topology_high',
    }),
    new DirectionCheck({
      expected: FORWARD,
      allowOpposite: true,
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new SymmetryCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new ChainPositionCheck({
      expectedPosition: 3,
      weight: W_CHAIN_POS,
      weightKey: 'chain_position',
    }),
    new VerticalPositionCheck({
      expectedMin: 0.0,
      expectedMax: 0.3,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new RelativeVerticalCheck({
      referenceRole: 'Pelvis',
      expectedRelation: 'below',
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['LeftToe'] = [
    new SideMatchCheck({
      expectedSide: 'Left',
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints('LeftToe'),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new DepthCheck({
      expected: [4, 6],
      relativeTo: 'Pelvis',
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new ToeParentCheck({
      weight: W_CONSTRAINT_HIGH,
      weightKey: 'constraint_high',
    }),
    new ToeDirectionCheck({ weight: W_DIRECTION, weightKey: 'direction' }),
  ];

  checks['LeftHock'] = [
    ..._legChecks('LeftHock', 'Left', DOWN, 3, true, 5.0),
    new VerticalPositionCheck({
      expectedMin: 0.0,
      expectedMax: 0.35,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Pelvis',
      expectedMin: -0.65,
      expectedMax: -0.2,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  // === RIGHT LEG ===
  checks['RightUpperLeg'] = [
    ..._legChecks('RightUpperLeg', 'Right', DOWN, 1),
    new VerticalPositionCheck({
      expectedMin: 0.1,
      expectedMax: 0.65,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Pelvis',
      expectedMin: -0.35,
      expectedMax: 0.0,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['RightLowerLeg'] = [
    ..._legChecks('RightLowerLeg', 'Right', DOWN, 2, true, 5.0),
    new VerticalPositionCheck({
      expectedMin: 0.0,
      expectedMax: 0.45,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Pelvis',
      expectedMin: -0.5,
      expectedMax: -0.1,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['RightFoot'] = [
    new SideMatchCheck({
      expectedSide: 'Right',
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints('RightFoot'),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new ChildCountCheck({
      expected: _getChildRange('RightFoot'),
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new DepthCheck({
      expected: [3, 5],
      relativeTo: 'Pelvis',
      weight: W_TOPOLOGY_HIGH,
      weightKey: 'topology_high',
    }),
    new DirectionCheck({
      expected: FORWARD,
      allowOpposite: true,
      weight: W_DIRECTION,
      weightKey: 'direction',
    }),
    new SymmetryCheck({ weight: W_SYMMETRY, weightKey: 'symmetry' }),
    new ChainPositionCheck({
      expectedPosition: 3,
      weight: W_CHAIN_POS,
      weightKey: 'chain_position',
    }),
    new VerticalPositionCheck({
      expectedMin: 0.0,
      expectedMax: 0.3,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new RelativeVerticalCheck({
      referenceRole: 'Pelvis',
      expectedRelation: 'below',
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  checks['RightToe'] = [
    new SideMatchCheck({
      expectedSide: 'Right',
      weight: 0.5,
      required: true,
      weightKey: 'side_match',
    }),
    new NamingMatchCheck({
      hints: _getHints('RightToe'),
      weight: W_NAMING,
      weightKey: 'naming',
    }),
    new DepthCheck({
      expected: [4, 6],
      relativeTo: 'Pelvis',
      weight: W_TOPOLOGY,
      weightKey: 'topology',
    }),
    new ToeParentCheck({
      weight: W_CONSTRAINT_HIGH,
      weightKey: 'constraint_high',
    }),
    new ToeDirectionCheck({ weight: W_DIRECTION, weightKey: 'direction' }),
  ];

  checks['RightHock'] = [
    ..._legChecks('RightHock', 'Right', DOWN, 3, true, 5.0),
    new VerticalPositionCheck({
      expectedMin: 0.0,
      expectedMax: 0.35,
      weight: W_SPATIAL_LIMB,
      weightKey: 'spatial_limb',
    }),
    new AnchorRelativePositionCheck({
      anchorRole: 'Pelvis',
      expectedMin: -0.65,
      expectedMax: -0.2,
      weight: W_SPATIAL_REL,
      weightKey: 'spatial_relative',
    }),
  ];

  // === FINGERS ===
  for (const finger of FINGER_ROLE_NAMES) {
    if (finger !== 'Thumb') {
      const leftMetacarpalRole = `Left${finger}Metacarpal`;
      checks[leftMetacarpalRole] = _fingerChecks(leftMetacarpalRole, 1);
      const rightMetacarpalRole = `Right${finger}Metacarpal`;
      checks[rightMetacarpalRole] = _fingerChecks(rightMetacarpalRole, 1);
    }

    for (let i = 1; i <= 3; i++) {
      const leftRole = `Left${finger}${i}`;
      checks[leftRole] = _fingerChecks(leftRole, i);
      const rightRole = `Right${finger}${i}`;
      checks[rightRole] = _fingerChecks(rightRole, i);
    }
  }

  return checks;
}

export const ROLE_CHECKS: Record<string, Check[]> = buildRoleChecks();

export function getChecksForRole(role: string): Check[] {
  return ROLE_CHECKS[role] ?? [];
}

export function getAllRoles(): string[] {
  return Object.keys(ROLE_CHECKS);
}

// ═══════════════════════════════════════════════════════════════════════
// EVALUATOR
// ═══════════════════════════════════════════════════════════════════════

export class RoleEvaluator {
  evaluate(ctx: CheckContext): EvaluationResult {
    const checks = getChecksForRole(ctx.role);
    if (checks.length === 0) {
      return new EvaluationResult(0.5, false, []);
    }

    const results: [Check, CheckResult][] = [];

    for (const check of checks) {
      let result: CheckResult;
      try {
        result = check.evaluate(ctx);
      } catch (e) {
        const msg =
          e instanceof Error ? e.message.slice(0, 50) : String(e).slice(0, 50);
        result = CheckResult.neutral(0.5, `Check error: ${msg}`);
      }

      results.push([check, result]);

      if (check.required && !result.passed) {
        return new EvaluationResult(0.0, true, results);
      }
    }

    const score = this._computeWeightedScore(results, ctx);
    return new EvaluationResult(score, false, results);
  }

  private _computeWeightedScore(
    results: [Check, CheckResult][],
    ctx: CheckContext,
  ): number {
    if (results.length === 0) return 0.5;

    const weights = results.map(([check]) => check.getEffectiveWeight(ctx));
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    if (totalWeight <= 0) return 0.5;

    let weightedSum = 0;
    for (let i = 0; i < results.length; i++) {
      weightedSum += weights[i] * results[i][1].score;
    }
    return Math.max(0.0, Math.min(1.0, weightedSum / totalWeight));
  }

  evaluateCandidates(
    skeleton: Skeleton,
    role: string,
    candidateIndices: number[],
    baseContext?: Partial<CheckContext>,
  ): [number, EvaluationResult][] {
    const base = baseContext ?? {};
    const results: [number, EvaluationResult][] = [];

    for (const boneIdx of candidateIndices) {
      const ctx: CheckContext = {
        skeleton,
        boneIdx,
        role,
        ...base,
      };
      const result = this.evaluate(ctx);
      results.push([boneIdx, result]);
    }

    results.sort((a, b) => b[1].score - a[1].score);
    return results;
  }

  findBestCandidate(
    skeleton: Skeleton,
    role: string,
    candidateIndices: number[],
    baseContext?: Partial<CheckContext>,
    minScore = 0.0,
  ): [number, EvaluationResult] | null {
    if (candidateIndices.length === 0) return null;

    const results = this.evaluateCandidates(
      skeleton,
      role,
      candidateIndices,
      baseContext,
    );
    if (results.length === 0) return null;

    const best = results[0];
    if (best[1].score >= minScore && !best[1].disqualified) return best;
    return null;
  }
}

let _evaluator: RoleEvaluator | null = null;

function getEvaluator(): RoleEvaluator {
  if (!_evaluator) _evaluator = new RoleEvaluator();
  return _evaluator;
}

export function evaluateRole(ctx: CheckContext): EvaluationResult {
  return getEvaluator().evaluate(ctx);
}
